import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommandExec, CommandsConfiguration, CommandsSchema} from '@iotsens/commands/src/lib/_shared';
import {TranslocoService} from '@ngneat/transloco';
import {MessageService} from 'primeng/api';
import {Subscription} from 'rxjs';
import {AuthGuard} from 'src/app/common/auth-guard.service';
import {Machine} from 'src/app/model/machine';
import {MachineDetailsService} from 'src/app/services/machine-details.service';
import {MachinesService} from 'src/app/services/machines.service';
import {MachineTechnicalInfo} from "../../../../model/custom/machine/machine-technical-info";
import {MachineType} from "../../../../model/machine-type";
import {CommandPersistRequest} from "../../../../model/requests/command-persist-request";


@Component({
  selector: 'app-machine-details-config',
  templateUrl: './machine-details-config.component.html',
  styleUrls: ['./machine-details-config.component.scss']
})
export class MachineDetailsConfigComponent implements OnInit, OnDestroy {

  public machine: Machine;
  public isMachineLoading: boolean = true;
  public machineCommandSchema: CommandsSchema;
  public machineTechnicalInfo: MachineTechnicalInfo;
  public configuration: CommandsConfiguration;

  private readonly SAVE: string = 'common.save';

  private machineDetailsSubscription: Subscription;


  constructor(
    private readonly machineDetailsService: MachineDetailsService,
    private readonly machineService: MachinesService,
    private readonly messageService: MessageService,
    private readonly i18n: TranslocoService,
    public authGuard: AuthGuard
  ) {
  }

  ngOnInit(): void {
    this.machineDetailsSubscription = this.machineDetailsService.$notifier.subscribe(machineDetails => {
      this.machine = machineDetails.machine;
      if (machineDetails.machineLoading === false) {
        this.setConfiguration();
        this.machineService.getMachineCommandSchemasByUuid(machineDetails.machine.machineUuid).subscribe(response => {
          this.machineCommandSchema = (response.commandSchemas != null) ? response.commandSchemas[0] : null;
          this.machineTechnicalInfo = response.machineTechnicalInfo;
          this.isMachineLoading = false;
        });
      }
    });
  }

  private setConfiguration(): void {
    this.i18n.selectTranslation().subscribe(() => {
      this.configuration = {
        classes: 'm-auto md:col-6',
        commands: {
          setSweeperEnabled: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                enabled: {
                  name: this.i18n.translate('attribute.setSweeperEnabled'),
                  labels: {
                    '0': this.i18n.translate('command.value.disabled'),
                    '1': this.i18n.translate('command.value.enabled')
                  }
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setSweeperMode: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                mode: {
                  name: this.i18n.translate('attribute.setSweeperMode'),
                  labels: {
                    '0': this.i18n.translate('command.value.continuous'),
                    '1': this.i18n.translate('command.value.discontinuous')
                  }
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setSweeperSpeed: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                speed: {
                  name: this.i18n.translate('attribute.setSweeperSpeed'),
                  labels: {
                    '0': this.i18n.translate('command.value.stop'),
                    '1': this.i18n.translate('command.value.slow'),
                    '2': this.i18n.translate('command.value.normal'),
                    '3': this.i18n.translate('command.value.fast')
                  }
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setWasteEnabled: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                enabled: {
                  name: this.i18n.translate('attribute.setWasteEnabled'),
                  labels: {
                    '0': this.i18n.translate('command.value.disabled'),
                    '1': this.i18n.translate('command.value.enabled')
                  }
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setWasteLimit: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                limit: {
                  name: this.i18n.translate('attribute.setWasteLimit')
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setLanguage: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                code: {
                  name: this.i18n.translate('attribute.setLanguage'),
                  labels: this.getLanguages(this.machine.model.modelName)
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setFruitsPro: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                amount: {
                  name: this.i18n.translate('attribute.setFruitsPro')
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setLockPro: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                enabled: {
                  name: this.i18n.translate('attribute.setLockPro'),
                  labels: {
                    '0': this.i18n.translate('command.value.disabled'),
                    '1': this.i18n.translate('command.value.enabled')
                  }
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setSelUpDownDisc: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                state: {
                  name: this.i18n.translate('attribute.setSelUpDownDisc'),
                  labels: {
                    '0': this.i18n.translate('command.value.off'),
                    '1': this.i18n.translate('command.value.up'),
                    '2': this.i18n.translate('command.value.down')
                  }
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setUpValue: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                amount: {
                  name: this.i18n.translate('attribute.setUpValue')
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setDownValueDisc: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                amount: {
                  name: (this.machine.model.modelName === 'Versatile Pro') ? this.i18n.translate('attribute.setDiscValue') : this.i18n.translate('attribute.setDownValue')
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setCashlessModeFruitNumber: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                amount: {
                  name: this.i18n.translate('attribute.setCashlessModeFruitNumber')
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setPinCt: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                pin: {
                  name: this.i18n.translate('attribute.setPinCt')
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setPinMaster: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                pin: {
                  name: this.i18n.translate('attribute.setPinMaster')
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setPinUser: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                pin: {
                  name: this.i18n.translate('attribute.setPinUser')
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setPinSat: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                pin: {
                  name: this.i18n.translate('attribute.setPinSat')
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setIotTimeout: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                minutes: {
                  name: this.i18n.translate('attribute.setIotTimeout')
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setIotSelControl: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                mode: {
                  name: this.i18n.translate('attribute.setIotSelControl'),
                  labels: {
                    '0': this.i18n.translate('command.value.off'),
                    '1': this.i18n.translate('command.value.ppuOn'),
                    '2': this.i18n.translate('command.value.iotOn')
                  }
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          setCommTech: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              command: '',
              params: {
                mode: {
                  name: this.i18n.translate('attribute.setCommTech'),
                  labels: {
                    '0': this.i18n.translate('command.value.gprs'),
                    '1': this.i18n.translate('command.value.wifi')
                  }
                }
              },
              button: this.i18n.translate(this.SAVE)
            }
          },
          reboot: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              button: this.i18n.translate('attribute.reboot')
            }
          },
          restoreFifo: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              button: this.i18n.translate('attribute.restoreFifo')
            }
          },
          getMemmap: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              button: this.i18n.translate('attribute.getMemmap'),
            }
          },
          machineResetPartialFruitCounter: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              button: this.i18n.translate('attribute.machineResetPartialFruitCounter')
            }
          },
          machineCpuReset: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              button: this.i18n.translate('attribute.machineCpuReset')
            }
          },
          machineEnable: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              button: this.i18n.translate('attribute.machineEnable')
            }
          },
          machineDisable: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              button: this.i18n.translate('attribute.machineDisable')
            }
          },
          machineFruitsCashlessReset: {
            inline: true,
            buttonClasses: 'w-10rem',
            literals: {
              button: this.i18n.translate('attribute.machineFruitsCashlessReset')
            }
          }
        }
      }
    });
  }

  private getLanguages(modelName: string): any {
    if (modelName === 'Speed Up' || modelName === 'Speed S +plus') {
      return {
        '0': this.i18n.translate('command.language.czech'),
        '1': this.i18n.translate('command.language.danish'),
        '2': this.i18n.translate('command.language.german'),
        '3': this.i18n.translate('command.language.english'),
        '4': this.i18n.translate('command.language.spanish'),
        '5': this.i18n.translate('command.language.french'),
        '6': this.i18n.translate('command.language.italian'),
        '7': this.i18n.translate('command.language.hungarian'),
        '8': this.i18n.translate('command.language.dutch'),
        '9': this.i18n.translate('command.language.norwegian'),
        '10': this.i18n.translate('command.language.polish'),
        '11': this.i18n.translate('command.language.portuguese'),
        '12': this.i18n.translate('command.language.romanian'),
        '13': this.i18n.translate('command.language.swedish'),
        '14': this.i18n.translate('command.language.turkish'),
        '15': this.i18n.translate('command.language.bulgarian'),
        '16': this.i18n.translate('command.language.greek'),
        '17': this.i18n.translate('command.language.russian'),
        '18': this.i18n.translate('command.language.ukrainian'),
        '19': this.i18n.translate('command.language.chinese'),
        '20': this.i18n.translate('command.language.korean'),
        '21': this.i18n.translate('command.language.japanese'),
        '22': this.i18n.translate('command.language.arabic'),
        '23': this.i18n.translate('command.language.hebrew'),
      }
    } else { // Versatile Pro
      return {
        '0': this.i18n.translate('command.language.spanish'),
        '1': this.i18n.translate('command.language.english'),
        '2': this.i18n.translate('command.language.french'),
        '3': this.i18n.translate('command.language.german'),
        '4': this.i18n.translate('command.language.italian'),
        '5': this.i18n.translate('command.language.hungarian'),
        '6': this.i18n.translate('command.language.portuguese'),
        '7': this.i18n.translate('command.language.polish'),
        '8': this.i18n.translate('command.language.turkish'),
        '9': this.i18n.translate('command.language.dutch'),
        '10': this.i18n.translate('command.language.czech'),
        '11': this.i18n.translate('command.language.danish'),
        '12': this.i18n.translate('command.language.norwegian'),
        '13': this.i18n.translate('command.language.swedish'),
        '14': this.i18n.translate('command.language.romanian'),
        '15': this.i18n.translate('command.language.bulgarian'),
        '16': this.i18n.translate('command.language.greek'),
        '17': this.i18n.translate('command.language.russian'),
        '18': this.i18n.translate('command.language.ukrainian'),
        '19': this.i18n.translate('command.language.korean'),
        '20': this.i18n.translate('command.language.japanese'),
        '21': this.i18n.translate('command.language.arabic'),
        '22': this.i18n.translate('command.language.chinese'),
        '23': this.i18n.translate('command.language.hebrew'),
      }
    }
  }

  executeCommand(event: CommandExec): void {
    if (event.commandName == 'setCommTech') {
      if (event.parameters.mode === '0') {
        if (!this.machineTechnicalInfo.mobileApn) {
          const summary = this.i18n.translate('error.summary.commandForbidden');
          const detail = this.i18n.translate('error.command.gprs');
          this.messageService.add({severity: 'error', summary, detail, sticky: true});
          return;
        }
      } else if (!this.machineTechnicalInfo.wifiStaSsid || !this.machineTechnicalInfo.wifiStaPass) {
        const summary = this.i18n.translate('error.summary.commandForbidden');
        const detail = this.i18n.translate('error.command.wifi');
        this.messageService.add({severity: 'error', summary, detail, sticky: true});
        return;
      }
    }
    const persistData: CommandPersistRequest = this.buildPersistData(event);
    this.machineService.executeMachineCommand(this.machine.machineUuid, this.machineCommandSchema.name,
      event.commandName, persistData).subscribe(() => {
        const summary = this.i18n.translate('success.generic.changesApplied');
        const detail = this.i18n.translate('success.command.executed');
        this.messageService.add({severity: 'success', summary, detail, sticky: false});
      }, () => {
        const summary = this.i18n.translate('error.summary.unexpected');
        const detail = this.i18n.translate('error.command.executed');
        this.messageService.add({severity: 'error', summary, detail, sticky: true});
      });
  }

  private buildPersistData(event: CommandExec): CommandPersistRequest {
    const command: any = this.machineCommandSchema.commands[event.commandName];
    let oldParameters: {[key: string]: string} = {};
    Object.entries(event.parameters).forEach((entry: [string, string]) => {
      let oldValue = command.params[entry[0]]?.value;
      if (oldValue != null) {
        oldParameters[entry[0]] = oldValue;
      }
    });

    return {
      parameters: event.parameters,
      oldParameters: oldParameters
    }
  }

  ngOnDestroy(): void {
    if (this.machineDetailsSubscription != null) this.machineDetailsSubscription.unsubscribe();
  }

  protected readonly MachineType = MachineType;

}
